import { QuestionBodyPartnerInterest } from '@/features/advisor-onboarding/questionnaire/components/question-body-partner-interest';
import { QuestionScreenLayout } from '@/features/advisor-onboarding/questionnaire/components/question-screen-layout';

import { useQuestionScreen } from '@/features/advisor-onboarding/questionnaire/contexts/question-screen-context';
import { usePartnerInterestQuestion } from '@/features/advisor-onboarding/questionnaire/hooks/use-partner-interest-question';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useIsDistributionPartnerContact } from '@/features/distribution-partners';
import { useIsPilotRestricted } from '@/shared/hooks/use-active-pilot-program';

import type { BgaPartnerInterest } from '@shared';

export function PartnerInterestScreen() {
  const { resolved, answers, isSubmitting, submitAnswerWithoutAdvance, advance } =
    useQuestionScreen();

  // Only offer the inline partner picker when distribution partners are available to this advisor;
  // otherwise fall back to the "we'll reach out" confirmation. Advisors whose program withholds
  // the capability shouldn't reach this screen at all, but the picker refuses to render for them
  // regardless, so a stale bundle can't hand them one.
  const { flags } = useFeatureFlags();
  const { hideDistributionPartners } = useIsDistributionPartnerContact();
  const isPilotRestricted = useIsPilotRestricted('distribution-partners');
  const canSelectPartners =
    !!flags.distributionPartnersEnabled && !hideDistributionPartners && !isPilotRestricted;

  const initialValue = (answers.bga_partner_interest as BgaPartnerInterest | undefined) ?? null;

  const { value, confirmedValue, select, confirm } = usePartnerInterestQuestion({
    initialValue,
    submitWithoutAdvance: submitAnswerWithoutAdvance,
    advance,
  });

  return (
    <QuestionScreenLayout
      showNext={!!confirmedValue}
      nextLabel="Continue"
      onNext={confirm}
    >
      <QuestionBodyPartnerInterest
        options={resolved.options}
        value={value}
        confirmedValue={confirmedValue}
        onSelect={select}
        canSelectPartners={canSelectPartners}
        disabled={isSubmitting}
      />
    </QuestionScreenLayout>
  );
}
